if (!customElements.get('predictive-search')) {
class PredictiveSearch extends SearchForm {
  static cachedResults = new Map();

  constructor() {
    super();
    this.searchTerm = '';
    this.renderedQueryKey = null;
    this.isOpen = false;
    this.abortController = null;
    this.predictiveListenersBound = false;
    this.connectionVersion = 0;
    this._handleFocus = this.onFocus.bind(this);
    this._handleFocusOut = this.onFocusOut.bind(this);
    this._handleClick = this.openMobileSearch.bind(this);
    this._handleDocumentClick = this.onDocumentClick.bind(this);
    this._handleKeyup = this.onKeyup.bind(this);
    this._handleKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.connectionVersion += 1;
    super.connectedCallback();

    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.mobSearchIcon = document.querySelector('[data-toggleMobSearch]');
    this.predictiveSearchWrapper = this.closest('.predictive-search-wrapper');
    this.html = document.documentElement;
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text') || '';

    if (!this.input || !this.predictiveSearchResults || this.predictiveListenersBound) return;

    this.input.addEventListener('focus', this._handleFocus);
    this.addEventListener('focusout', this._handleFocusOut);
    this.mobSearchIcon?.addEventListener('click', this._handleClick);
    this.addEventListener('keyup', this._handleKeyup);
    this.addEventListener('keydown', this._handleKeydown);
    document.addEventListener('click', this._handleDocumentClick);
    this.predictiveListenersBound = true;
  }

  disconnectedCallback() {
    this.connectionVersion += 1;
    this.abortPendingRequest();

    if (this.predictiveListenersBound) {
      this.input?.removeEventListener('focus', this._handleFocus);
      this.removeEventListener('focusout', this._handleFocusOut);
      this.mobSearchIcon?.removeEventListener('click', this._handleClick);
      this.removeEventListener('keyup', this._handleKeyup);
      this.removeEventListener('keydown', this._handleKeydown);
      document.removeEventListener('click', this._handleDocumentClick);
    }

    this.predictiveListenersBound = false;
    this.predictiveSearchResults = null;
    this.mobSearchIcon = null;
    this.predictiveSearchWrapper = null;
    this.html = null;
    this.statusElement = null;
    clearTimeout(this.liveRegionTimeout);
    super.disconnectedCallback();
  }

  static normalizeQuery(searchTerm) {
    return searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  hasCurrentResults() {
    return this.hasAttribute('results') &&
      this.renderedQueryKey === PredictiveSearch.normalizeQuery(this.getQuery());
  }

  onInput() {
    // Invalidate selection before debounce so Enter cannot activate an older query.
    this.clearActiveOption();
    if (!this.hasCurrentResults()) this.close({ clear: true });
    super.onInput();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!newSearchTerm.length) {
      this.searchTerm = '';
      this.close({ clear: true });
      return;
    }

    this.searchTerm = newSearchTerm;
    if (this.hasCurrentResults()) {
      this.open();
      return;
    }
    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    super.onFormSubmit(event);

    if (this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    const closeMobile = window.matchMedia('(max-width: 991px)').matches;
    this.searchTerm = '';
    this.close({ clear: true, closeMobile });

    if (closeMobile) this.mobSearchIcon?.focus();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.cancelPendingChange();

    if (this.html) this.html.style.scrollPaddingTop = 'unset';
    if (!currentSearchTerm.length) return;

    this.searchTerm = currentSearchTerm;
    if (this.hasCurrentResults()) {
      this.open();
    } else {
      this.getSearchResults(currentSearchTerm);
    }
  }

  onFocusOut() {
    const connectionVersion = this.connectionVersion;

    if (this.html) this.html.style.scrollPaddingTop = '';
    setTimeout(() => {
      if (
        this.isConnected &&
        connectionVersion === this.connectionVersion &&
        !this.contains(document.activeElement) &&
        (this.isOpen || this.hasAttribute('loading') || this.inputTimeout !== null)
      ) {
        this.close({ closeMobile: true });
      }
    });
  }

  onDocumentClick(event) {
    if (this.contains(event.target) || this.mobSearchIcon?.contains(event.target)) return;
    if (!this.isOpen && !this.hasAttribute('loading') && this.inputTimeout === null) return;

    this.close({ closeMobile: true });
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'Escape') {
      event.preventDefault();
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close({ clear: true });
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Escape':
        event.preventDefault();
        this.input?.focus();
        this.close();
        break;
    }
  }

  getVisibleOptions() {
    return Array.from(this.querySelectorAll('[role="option"]')).filter(
      (option) => option.offsetParent !== null,
    );
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const options = this.getVisibleOptions();
    if (!options.length) return;

    const selectedOption = this.querySelector('[role="option"][aria-selected="true"]');
    const selectedIndex = options.indexOf(selectedOption);
    const offset = direction === 'up' ? -1 : 1;
    const nextIndex = selectedIndex === -1
      ? direction === 'up'
        ? options.length - 1
        : 0
      : (selectedIndex + offset + options.length) % options.length;

    this.clearActiveOption();
    const activeOption = options[nextIndex];
    activeOption.setAttribute('aria-selected', 'true');
    this.input?.setAttribute('aria-activedescendant', activeOption.id);
    activeOption.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
  }

  selectOption() {
    this.querySelector(
      '[role="option"][aria-selected="true"] [data-predictive-search-option-target]',
    )?.click();
  }

  clearActiveOption() {
    this.querySelector('[role="option"][aria-selected="true"]')?.setAttribute(
      'aria-selected',
      'false',
    );
    this.input?.setAttribute('aria-activedescendant', '');
  }

  abortPendingRequest() {
    this.abortController?.abort();
    this.abortController = null;
  }

  async getSearchResults(searchTerm) {
    const searchFields =
      'author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor';
    const queryKey = PredictiveSearch.normalizeQuery(searchTerm);
    const connectionVersion = this.connectionVersion;

    this.close({ clear: true });
    this.setLoadingState(true);

    const cachedResult = PredictiveSearch.cachedResults.get(queryKey);
    if (cachedResult) {
      if (PredictiveSearch.normalizeQuery(this.getQuery()) === queryKey) {
        this.renderSearchResults(cachedResult, queryKey);
      }
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;

    try {
      const response = await fetch(
        `${window.routes.predictive_searh}?q=${encodeURIComponent(searchTerm)}&resources[options][fields]=${searchFields}&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search`,
        { signal: controller.signal },
      );

      if (!response.ok) throw new Error(`Predictive search request failed with ${response.status}.`);

      const text = await response.text();
      const section = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');

      if (!section) throw new Error('Predictive search response did not include its section.');
      if (
        !this.isConnected ||
        connectionVersion !== this.connectionVersion ||
        controller !== this.abortController ||
        PredictiveSearch.normalizeQuery(this.getQuery()) !== queryKey
      ) {
        return;
      }

      const resultsMarkup = section.innerHTML;
      PredictiveSearch.cachedResults.set(queryKey, resultsMarkup);
      this.renderSearchResults(resultsMarkup, queryKey);
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 20) return;

      if (connectionVersion === this.connectionVersion && controller === this.abortController) {
        this.close();
      }
    } finally {
      if (controller === this.abortController) {
        this.abortController = null;
        this.setLoadingState(false);
      }
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.setLiveRegionText(this.loadingText);
    } else {
      this.removeAttribute('loading');
    }
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || !statusText) return;

    clearTimeout(this.liveRegionTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    this.liveRegionTimeout = setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent.trim());
  }

  renderSearchResults(resultsMarkup, queryKey) {
    if (!this.predictiveSearchResults) return;

    this.clearActiveOption();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.renderedQueryKey = queryKey;
    this.setAttribute('results', 'true');
    this.setLoadingState(false);
    window.StampedFn?.loadBadges?.();
    this.open();
    this.setLiveRegionResults();
  }

  open() {
    if (!this.predictiveSearchResults || !this.hasCurrentResults()) return;

    this.predictiveSearchResults.classList.add('show-results');
    this.setAttribute('open', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close({ clear = false, closeMobile = false } = {}) {
    // Dismissal invalidates both work waiting for debounce and any in-flight response.
    this.cancelPendingChange();
    this.abortPendingRequest();
    if (clear) this.clearResults();

    this.clearActiveOption();
    this.predictiveSearchResults?.classList.remove('show-results');
    this.removeAttribute('open');
    this.setLoadingState(false);
    this.input?.setAttribute('aria-expanded', 'false');
    this.isOpen = false;

    if (closeMobile) this.closeMobileSearch();
  }

  clearResults() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
    this.renderedQueryKey = null;
    this.removeAttribute('results');
  }

  openMobileSearch(event) {
    event.preventDefault();
    if (!this.predictiveSearchWrapper || !this.input) return;

    this.predictiveSearchWrapper.classList.remove('d-none');
    if (this.hasAttribute('results')) this.open();
    this.input.focus();
    document.body.classList.add('scroll-fixed');
  }

  closeMobileSearch() {
    this.predictiveSearchWrapper?.classList.add('d-none');
    document.body.classList.remove('scroll-fixed');
    this.input?.blur();
  }

  showResults() {
    this.open();
  }

  hideResults(clearResults = false) {
    this.close({ clear: clearResults, closeMobile: true });
  }
}

  customElements.define('predictive-search', PredictiveSearch);
}
